import { gsap } from 'gsap';
import { Graphics } from 'pixi.js';
import type { BattleScene } from '../../../render/BattleScene';

// Placeholder shockwave crack (no art yet - primitive + GSAP per Phase 1). A
// jagged fissure tears along the ground from the attacker's feet toward the
// opponent. The returned promise resolves the instant the crack REACHES the
// far end so the caller can land damage / painAir in sync with the arrival;
// the crack then fades out fire-and-forget on its own.

// Length of one zigzag step and how far it kicks off the ground line.
const SEGMENT_LEN = 26;
const ZIGZAG = 5;
// Two-pass stroke: dark outer fissure with a hot inner core so it reads as an
// energized shockwave, not just a scratch.
const OUTER_WIDTH = 7;
const OUTER_COLOR = 0x1a1206;
const INNER_WIDTH = 3;
const INNER_COLOR = 0xff7722;
// Linger then fade after arrival (BATTLE-pace seconds - globalTimeline scales).
const FADE_DELAY = 0.12;
const FADE_DURATION = 0.3;

type Point = { x: number; y: number };

export type GroundCrackOptions = {
  scene: BattleScene;
  fromX: number;
  toX: number;
  y: number;
  // Travel time in BATTLE-pace seconds (gsap globalTimeline timeScale slows it).
  durationSec: number;
};

export function travelGroundCrack(opts: GroundCrackOptions): Promise<void> {
  const points = buildJaggedPath(opts.fromX, opts.toX, opts.y);
  const crack = new Graphics();
  opts.scene.spawnEffect(crack);

  const progress = { t: 0 };
  return new Promise<void>((resolve) => {
    gsap.to(progress, {
      t: 1,
      duration: opts.durationSec,
      ease: 'power1.out',
      onUpdate: () => drawCrack(crack, points, progress.t),
      onComplete: () => {
        // Arrival: caller fires damage now. Fade is detached so it doesn't
        // hold up the hit reaction.
        resolve();
        gsap.to(crack, {
          alpha: 0,
          delay: FADE_DELAY,
          duration: FADE_DURATION,
          onComplete: () => {
            crack.parent?.removeChild(crack);
            crack.destroy();
          },
        });
      },
    });
  });
}

// Points hug the ground at both ends and zigzag slightly in between. Render-side
// Math.random is fine here (no determinism contract outside the engine).
function buildJaggedPath(fromX: number, toX: number, y: number): Point[] {
  const dx = toX - fromX;
  const dist = Math.abs(dx);
  const n = Math.max(2, Math.round(dist / SEGMENT_LEN));
  const points: Point[] = [];
  for (let i = 0; i <= n; i++) {
    const x = fromX + (dx * i) / n;
    const ends = i === 0 || i === n;
    const offsetY = ends ? 0 : (Math.random() - 0.5) * 2 * ZIGZAG;
    points.push({ x, y: y + offsetY });
  }
  return points;
}

// Redraws the fissure up to fraction t (0..1) of its length, interpolating the
// leading edge so growth looks continuous rather than snapping segment by segment.
function drawCrack(crack: Graphics, points: Point[], t: number): void {
  crack.clear();
  if (points.length < 2) return;
  const segs = points.length - 1;
  const reach = t * segs;
  const full = Math.floor(reach);
  const frac = reach - full;

  const trace = (): void => {
    const first = points[0];
    if (!first) return;
    crack.moveTo(first.x, first.y);
    for (let i = 1; i <= Math.min(full, segs); i++) {
      const p = points[i];
      if (p) crack.lineTo(p.x, p.y);
    }
    if (full < segs && frac > 0) {
      const a = points[full];
      const b = points[full + 1];
      if (a && b) crack.lineTo(a.x + (b.x - a.x) * frac, a.y + (b.y - a.y) * frac);
    }
  };

  trace();
  crack.stroke({ width: OUTER_WIDTH, color: OUTER_COLOR, cap: 'round', join: 'round' });
  trace();
  crack.stroke({ width: INNER_WIDTH, color: INNER_COLOR, cap: 'round', join: 'round' });
}
